import React from "react";
import CareWatchPreviewCard, {
  CareWatchPreviewRow,
} from "./CareWatchPreviewCard";

/**
 * What the analyser actually found, check by check.
 *
 * This is the evidence a manager triages on -- which checks failed, on which
 * participant, and in whose words. The API has always sent it as `checks`;
 * the web dock rendered only the incident-form preview, so the reasoning was
 * visible on mobile and nowhere on web.
 *
 * Ordered most severe first by the API, so it is rendered as given.
 */
// Older analyses stored a fixed instruction on the incident check and the
// card printed it under every finding. Those lines are not the model's words.
const CANNED_SUGGESTION = [
  "submit the incident form or correct the incident details",
  "enter the incident details on tab 1",
  "enter incident details on tab 1",
  "align incident details with the submitted form",
  "add a brief incident reference in the shift note",
  "update incident details on tab 1",
];

const readableSuggestion = (value) => {
  const text = String(value || "").trim();
  if (!text) return "";
  const lower = text.toLowerCase();
  if (CANNED_SUGGESTION.some((line) => lower.startsWith(line))) return "";
  // The AI model sometimes references shift-end answers by array index
  // ("answer index 2", "answer 2 points to"). Clean up so managers see
  // "answer #2" instead of the raw "answer index 2".
  return cleanAnswerIndex(text);
};

/** Strip raw array-index references the AI model sometimes emits. */
const cleanAnswerIndex = (text) =>
  text.replace(/\bindex\s+(\d+)/gi, "#$1");

const CareWatchFindings = ({ item, showOutcome = true }) => {
  const checks = Array.isArray(item?.checks) ? item.checks : [];
  if (checks.length === 0) return null;

  const categories = showOutcome && Array.isArray(item.categoryLabels) && item.categoryLabels.length
    ? item.categoryLabels
    : [];

  return (
    <CareWatchPreviewCard
      title="Findings"
      className="care-watch-findings"
    >
      {categories.length ? (
        <ul className="care-watch-findings__categories">
          {categories.map((label) => (
            <li key={label}>{label}</li>
          ))}
        </ul>
      ) : null}
      {checks.map((check, index) => (
        <CareWatchPreviewRow
          key={check.id || index}
          label={check.label || check.id}
        >
          {cleanAnswerIndex(String(check.summary || ""))}
          {/* The quoted note text the check was drawn from. Kept subordinate to
              the summary: it is corroboration, not the finding itself. */}
          {Array.isArray(check.evidence) && check.evidence.length > 0 ? (
            <ul className="care-watch-findings__evidence">
              {check.evidence.map((quote, i) => (
                <li key={i}>{quote}</li>
              ))}
            </ul>
          ) : null}
          {readableSuggestion(check.suggestion) ? (
            <p className="care-watch-findings__suggestion">
              {readableSuggestion(check.suggestion)}
            </p>
          ) : null}
        </CareWatchPreviewRow>
      ))}
    </CareWatchPreviewCard>
  );
};

export default CareWatchFindings;
